/**
 * Responsibility: turn a chain of surveyed stations into line segments.
 *
 * Emits `{x1, y1, z1, x2, y2, z2, t1, t2}` records, which is exactly what every building, bridge and
 * airfield in this world already emits — so the track needs no new emitter and no new material.
 *
 * ## What makes two lines read as a railway
 *
 * Two rails alone read as a road. What says railway is the **cross-hatching**: sleepers at a regular pitch,
 * closely spaced, running past under the window. So the sleeper pitch matters more here than the rails do,
 * and it is the one thing that would be worth spending more segments on.
 *
 * The shoulder lines outside the rails do the other half of the job. Without them the track is painted on
 * the ground; with them it sits on a raised bed, which is what makes a corridor read as engineered rather
 * than as a stripe.
 *
 * ## Why `t` runs across the track rather than up it
 *
 * Everywhere else `t` is a height fraction over a structure's own extent, which is meaningless for something
 * flat. Here it picks out structure instead: railheads bright, sleepers mid, ballast dim. The gradient is
 * being used to say what a line *is*, the same call `airfieldGeometry` made for its runway.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { RAILWAY } from '../config.js';

/** Colour fractions picking out the three parts of the track. Railheads are what the eye follows. */
const RAIL_TINT = 1;
const SLEEPER_TINT = 0.55;
const SHOULDER_TINT = 0.12;

/** A segment record in the shape `emitShapeSegments` consumes. */
function line([x1, y1, z1], [x2, y2, z2], t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2: t2 ?? t1 };
}

/**
 * Build the track over a run of stations.
 *
 * @param {Array<{x: number, z: number, height: number, heading: number}>} stations - Consecutive stations
 *   from `createSurveyor`. Order matters: rails are drawn between neighbours.
 * @param {object} [options]
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {Array<object>} Line segments.
 */
export function buildTrack(stations, { tuning: overrides = RAILWAY } = {}) {
  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  if (!Array.isArray(stations) || stations.length < 2) return [];

  const half = tuning.GAUGE / 2;
  const lift = tuning.RAIL_LIFT;
  const segments = [];

  /**
   * A point offset across the track at a station.
   *
   * Taken from the station's own heading rather than from the direction to its neighbour, so the two rails
   * stay parallel through a curve instead of pinching on the inside of it.
   */
  const across = (station, offset) => [
    station.x - Math.sin(station.heading) * offset,
    station.height + lift,
    station.z + Math.cos(station.heading) * offset,
  ];

  for (let i = 0; i < stations.length - 1; i += 1) {
    const from = stations[i];
    const to = stations[i + 1];

    // The two rails.
    for (const offset of [-half, half]) {
      segments.push(line(across(from, offset), across(to, offset), RAIL_TINT));
    }

    /*
      Ballast shoulders. Drawn at the formation rather than lifted with the rails, so they read as the edge of
      the bed the track sits on rather than as two more rails.
    */
    for (const offset of [-tuning.SHOULDER_HALF_WIDTH, tuning.SHOULDER_HALF_WIDTH]) {
      const a = across(from, offset);
      const b = across(to, offset);
      segments.push(line([a[0], from.height, a[2]], [b[0], to.height, b[2]], SHOULDER_TINT));
    }
  }

  /*
    Sleepers, on their own pitch in world units and interpolated along each segment.

    **Not one per station, which is what shipped first and read as a ladder.** Station spacing is a survey
    decision — how finely the route is walked — and sleeper pitch is a look decision, and tying them together
    meant the only way to get sleepers closer was to make the whole chain longer. Interpolating separates the
    two, so this can go as fine as it likes for free.
  */
  const overhang = half + tuning.SLEEPER_OVERHANG;
  const spacing = Math.hypot(stations[1].x - stations[0].x, stations[1].z - stations[0].z);
  const perSegment = Math.max(1, Math.round(spacing / tuning.SLEEPER_PITCH));

  for (let i = 0; i < stations.length - 1; i += 1) {
    const from = stations[i];
    const to = stations[i + 1];

    for (let step = 0; step < perSegment; step += 1) {
      const t = step / perSegment;

      /*
        A station's own fields, interpolated. Heading rather than the direction to the next station for the same
        reason the rails use it: at a curve the two differ, and a sleeper square to the chord instead of to the
        track would splay.
      */
      const at = {
        x: from.x + (to.x - from.x) * t,
        z: from.z + (to.z - from.z) * t,
        height: from.height + (to.height - from.height) * t,
        heading: from.heading + (to.heading - from.heading) * t,
      };

      segments.push(line(across(at, -overhang), across(at, overhang), SLEEPER_TINT));
    }
  }

  return segments;
}
